/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  CustomerDeletionResponseOutput,
  CustomerDetailResponseOutput,
  CustomerLifecycleResponseOutput,
  CustomerPageResponseOutput,
} from '../model';
import type {
  CustomerArchiveBody,
  CustomerCreateBody,
  CustomerDeleteBody,
  CustomerReactivateBody,
  CustomerRenameBody,
  ListCustomersParams,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListCustomersUrl = (params?: ListCustomersParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/customers?${stringifiedParams}`
    : `/api/v1/customers`;
};

export const listCustomers = async (
  params?: ListCustomersParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerPageResponseOutput> => {
  return validatedFetch<CustomerPageResponseOutput>(getListCustomersUrl(params), {
    ...options,
    method: 'GET',

    schema: CustomerPageResponseOutput,
  });
};

export const getListCustomersQueryKey = (params?: ListCustomersParams) => {
  return [`/api/v1/customers`, ...(params ? [params] : [])] as const;
};

export const getListCustomersQueryOptions = <
  TData = Awaited<ReturnType<typeof listCustomers>>,
  TError = unknown,
>(
  params?: ListCustomersParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomers>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCustomersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCustomers>>> = ({ signal }) =>
    listCustomers(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCustomers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListCustomersQueryResult = NonNullable<Awaited<ReturnType<typeof listCustomers>>>;
export type ListCustomersQueryError = unknown;

export function useListCustomers<
  TData = Awaited<ReturnType<typeof listCustomers>>,
  TError = unknown,
>(
  params?: ListCustomersParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listCustomers>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListCustomersQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateCustomerUrl = () => {
  return `/api/v1/customers`;
};

export const createCustomer = async (
  customerCreateBody: CustomerCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerDetailResponseOutput>(getCreateCustomerUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerCreateBody),
    schema: CustomerDetailResponseOutput,
  });
};

export const getCreateCustomerMutationKey = () => ['createCustomer'] as const;

export const getCreateCustomerMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCustomer>>,
    TError,
    CreateCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCustomer>>,
  TError,
  CreateCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getCreateCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCustomer>>,
    CreateCustomerMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createCustomer(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCustomerMutationResult = NonNullable<Awaited<ReturnType<typeof createCustomer>>>;
export type CreateCustomerMutationBody = CustomerCreateBody;
export type CreateCustomerMutationError = unknown;
export type CreateCustomerMutationVariables = { data: CustomerCreateBody };

export const useCreateCustomer = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCustomer>>,
    TError,
    CreateCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createCustomer>>,
  TError,
  CreateCustomerMutationVariables,
  TContext
> => {
  return useMutation(getCreateCustomerMutationOptions(options));
};
export const getGetCustomerUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}`;
};

export const getCustomer = async (
  customerId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerDetailResponseOutput> => {
  return validatedFetch<CustomerDetailResponseOutput>(getGetCustomerUrl(customerId), {
    ...options,
    method: 'GET',

    schema: CustomerDetailResponseOutput,
  });
};

export const getGetCustomerQueryKey = (customerId: string) => {
  return [`/api/v1/customers/${customerId}`] as const;
};

export const getGetCustomerQueryOptions = <
  TData = Awaited<ReturnType<typeof getCustomer>>,
  TError = unknown,
>(
  customerId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getCustomer>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCustomerQueryKey(customerId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCustomer>>> = ({ signal }) =>
    getCustomer(customerId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: customerId !== null && customerId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getCustomer>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetCustomerQueryResult = NonNullable<Awaited<ReturnType<typeof getCustomer>>>;
export type GetCustomerQueryError = unknown;

export function useGetCustomer<TData = Awaited<ReturnType<typeof getCustomer>>, TError = unknown>(
  customerId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getCustomer>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetCustomerQueryOptions(customerId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getRenameCustomerUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}`;
};

export const renameCustomer = async (
  customerId: string,
  customerRenameBody: CustomerRenameBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerDetailResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerDetailResponseOutput>(getRenameCustomerUrl(customerId), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerRenameBody),
    schema: CustomerDetailResponseOutput,
  });
};

export const getRenameCustomerMutationKey = () => ['renameCustomer'] as const;

export const getRenameCustomerMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameCustomer>>,
    TError,
    RenameCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof renameCustomer>>,
  TError,
  RenameCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getRenameCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof renameCustomer>>,
    RenameCustomerMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return renameCustomer(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RenameCustomerMutationResult = NonNullable<Awaited<ReturnType<typeof renameCustomer>>>;
export type RenameCustomerMutationBody = CustomerRenameBody;
export type RenameCustomerMutationError = unknown;
export type RenameCustomerMutationVariables = { customerId: string; data: CustomerRenameBody };

export const useRenameCustomer = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameCustomer>>,
    TError,
    RenameCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof renameCustomer>>,
  TError,
  RenameCustomerMutationVariables,
  TContext
> => {
  return useMutation(getRenameCustomerMutationOptions(options));
};
export const getDeleteCustomerUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}`;
};

export const deleteCustomer = async (
  customerId: string,
  customerDeleteBody: CustomerDeleteBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerDeletionResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerDeletionResponseOutput>(getDeleteCustomerUrl(customerId), {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerDeleteBody),
    schema: CustomerDeletionResponseOutput,
  });
};

export const getDeleteCustomerMutationKey = () => ['deleteCustomer'] as const;

export const getDeleteCustomerMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCustomer>>,
    TError,
    DeleteCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCustomer>>,
  TError,
  DeleteCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCustomer>>,
    DeleteCustomerMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return deleteCustomer(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCustomerMutationResult = NonNullable<Awaited<ReturnType<typeof deleteCustomer>>>;
export type DeleteCustomerMutationBody = CustomerDeleteBody;
export type DeleteCustomerMutationError = unknown;
export type DeleteCustomerMutationVariables = { customerId: string; data: CustomerDeleteBody };

export const useDeleteCustomer = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCustomer>>,
    TError,
    DeleteCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteCustomer>>,
  TError,
  DeleteCustomerMutationVariables,
  TContext
> => {
  return useMutation(getDeleteCustomerMutationOptions(options));
};
export const getArchiveCustomerUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}/archive`;
};

export const archiveCustomer = async (
  customerId: string,
  customerArchiveBody: CustomerArchiveBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerLifecycleResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerLifecycleResponseOutput>(getArchiveCustomerUrl(customerId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerArchiveBody),
    schema: CustomerLifecycleResponseOutput,
  });
};

export const getArchiveCustomerMutationKey = () => ['archiveCustomer'] as const;

export const getArchiveCustomerMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveCustomer>>,
    TError,
    ArchiveCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof archiveCustomer>>,
  TError,
  ArchiveCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getArchiveCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof archiveCustomer>>,
    ArchiveCustomerMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return archiveCustomer(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ArchiveCustomerMutationResult = NonNullable<
  Awaited<ReturnType<typeof archiveCustomer>>
>;
export type ArchiveCustomerMutationBody = CustomerArchiveBody;
export type ArchiveCustomerMutationError = unknown;
export type ArchiveCustomerMutationVariables = { customerId: string; data: CustomerArchiveBody };

export const useArchiveCustomer = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveCustomer>>,
    TError,
    ArchiveCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof archiveCustomer>>,
  TError,
  ArchiveCustomerMutationVariables,
  TContext
> => {
  return useMutation(getArchiveCustomerMutationOptions(options));
};
export const getReactivateCustomerUrl = (customerId: string) => {
  return `/api/v1/customers/${customerId}/reactivate`;
};

export const reactivateCustomer = async (
  customerId: string,
  customerReactivateBody: CustomerReactivateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<CustomerLifecycleResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<CustomerLifecycleResponseOutput>(getReactivateCustomerUrl(customerId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerReactivateBody),
    schema: CustomerLifecycleResponseOutput,
  });
};

export const getReactivateCustomerMutationKey = () => ['reactivateCustomer'] as const;

export const getReactivateCustomerMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reactivateCustomer>>,
    TError,
    ReactivateCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reactivateCustomer>>,
  TError,
  ReactivateCustomerMutationVariables,
  TContext
> => {
  const mutationKey = getReactivateCustomerMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reactivateCustomer>>,
    ReactivateCustomerMutationVariables
  > = (props) => {
    const { customerId, data } = props ?? {};

    return reactivateCustomer(customerId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ReactivateCustomerMutationResult = NonNullable<
  Awaited<ReturnType<typeof reactivateCustomer>>
>;
export type ReactivateCustomerMutationBody = CustomerReactivateBody;
export type ReactivateCustomerMutationError = unknown;
export type ReactivateCustomerMutationVariables = {
  customerId: string;
  data: CustomerReactivateBody;
};

export const useReactivateCustomer = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reactivateCustomer>>,
    TError,
    ReactivateCustomerMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof reactivateCustomer>>,
  TError,
  ReactivateCustomerMutationVariables,
  TContext
> => {
  return useMutation(getReactivateCustomerMutationOptions(options));
};
